import React from "react";
import {
  makeStyles,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@material-ui/core";
import ErrorIcon from "@material-ui/icons/Error";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { getLang } from "app/feature/constants";

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  header: {
    padding: theme.spacing(2),
    backgroundColor: "#fff3e0",
    borderLeft: `4px solid ${theme.palette.warning.main}`,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  headerIcon: {
    color: theme.palette.warning.main,
    fontSize: 24,
  },
  headerText: {
    fontWeight: 600,
    color: theme.palette.warning.dark,
  },
  tableContainer: {
    maxHeight: 400,
    border: `1px solid ${theme.palette.divider}`,
  },
  table: {
    minWidth: 500,
  },
  serialNumberCell: {
    fontFamily: "monospace",
    fontSize: "0.875rem",
    padding: theme.spacing(1, 2),
  },
  indexCell: {
    width: 80,
    textAlign: "center",
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  validChip: {
    backgroundColor: "#e8f5e8",
    color: "#2e7d32",
    fontSize: "0.75rem",
    height: 24,
  },
  invalidChip: {
    backgroundColor: "#ffebee",
    color: "#c62828",
    fontSize: "0.75rem",
    height: 24,
  },
  summary: {
    padding: theme.spacing(1, 2),
    backgroundColor: "#fafafa",
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  summaryText: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
  countBadges: {
    display: "flex",
    gap: theme.spacing(1),
  },
  validBadge: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.common.white,
    padding: theme.spacing(0.5, 1),
    borderRadius: 12,
    fontSize: "0.75rem",
    fontWeight: 600,
  },
  invalidBadge: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
    padding: theme.spacing(0.5, 1),
    borderRadius: 12,
    fontSize: "0.75rem",
    fontWeight: 600,
  },
}));

export default function InvalidSerialsTableComponent({
  invalidSerialNumbers = [],
  validSerialNumbers = [],
  allSerialsStatus = null,
  lang,
}) {
  const classes = useStyles();

  // conver OBJ to ARR if needed.
  const ensureArray = (data) => {
    if (Array.isArray(data)) {
      return data;
    } else if (data && typeof data === 'object') {
      // Convert OBVJ to ARR of values
      const converted = Object.values(data);
      return converted;
    }
    return [];
  };

  // Create comprehensive SN list with status
  let serialNumbersWithStatus = [];

  if (allSerialsStatus && allSerialsStatus.valid && allSerialsStatus.invalid) {
    // Use the comprehensive status data if available
    const validSerials = ensureArray(allSerialsStatus.valid);
    const invalidSerials = ensureArray(allSerialsStatus.invalid);
    
    validSerials.forEach(sn => {
      if (sn && typeof sn === 'string' && sn.trim() !== '') {
        serialNumbersWithStatus.push({ serialNumber: sn, status: 'valid' });
      }
    });
    
    invalidSerials.forEach(sn => {
      if (sn && typeof sn === 'string' && sn.trim() !== '') {
        serialNumbersWithStatus.push({ serialNumber: sn, status: 'invalid' });
      }
    });
  } else {
    // Fallback to separate arrays
    const safeValidSerialNumbers = ensureArray(validSerialNumbers)
      .filter(sn => sn && typeof sn === 'string' && sn.trim() !== '');
    
    const safeInvalidSerialNumbers = ensureArray(invalidSerialNumbers)
      .filter(sn => sn && typeof sn === 'string' && sn.trim() !== '');

    safeValidSerialNumbers.forEach(sn => {
      serialNumbersWithStatus.push({ serialNumber: sn, status: 'valid' });
    });
    
    safeInvalidSerialNumbers.forEach(sn => {
      serialNumbersWithStatus.push({ serialNumber: sn, status: 'invalid' });
    });
  }


  if (!serialNumbersWithStatus || serialNumbersWithStatus.length === 0) {
    return null;
  }

  // Sort to show invalid ones first, then valid ones
  serialNumbersWithStatus.sort((a, b) => {
    if (a.status === 'invalid' && b.status === 'valid') return -1;
    if (a.status === 'valid' && b.status === 'invalid') return 1;
    return a.serialNumber.localeCompare(b.serialNumber);
  });

  const validCount = serialNumbersWithStatus.filter(item => item.status === 'valid').length;
  const invalidCount = serialNumbersWithStatus.filter(item => item.status === 'invalid').length;

  return (
    <Paper className={classes.root} elevation={0}>
      <Box className={classes.header}>
        <ErrorIcon className={classes.headerIcon} />
        <Typography variant="subtitle2" className={classes.headerText}>
          {getLang(lang, "label.SERIAL_NUMBERS_VALIDATION_RESULT") || "Serial Numbers Validation Result"}
        </Typography>
        <div className={classes.countBadges}>
          {validCount > 0 && (
            <span className={classes.validBadge}>
              {validCount} Valid
            </span>
          )}
          {invalidCount > 0 && (
            <span className={classes.invalidBadge}>
              {invalidCount} Invalid
            </span>
          )}
        </div>
      </Box>

      <TableContainer className={classes.tableContainer}>
        <Table className={classes.table} size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell className={classes.indexCell}>
                <Typography variant="caption" style={{ fontWeight: 600 }}>
                  #
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="caption" style={{ fontWeight: 600 }}>
                  {getLang(lang, "label.SERIAL_NUMBER") || "Serial Number"}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="caption" style={{ fontWeight: 600 }}>
                  {getLang(lang, "label.STATUS") || "Status"}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {serialNumbersWithStatus.map((item, index) => (
              <TableRow key={index} hover>
                <TableCell className={classes.indexCell}>
                  {index + 1}
                </TableCell>
                <TableCell className={classes.serialNumberCell}>
                  {item.serialNumber}
                </TableCell>
                <TableCell align="center">
                  {item.status === 'valid' ? (
                    <Chip
                      icon={<CheckCircleIcon style={{ fontSize: 16 }} />}
                      label={getLang(lang, "label.VALID") || "Valid"}
                      size="small"
                      className={classes.validChip}
                    />
                  ) : (
                    <Chip
                      icon={<ErrorIcon style={{ fontSize: 16 }} />}
                      label={getLang(lang, "label.NOT_FOUND") || "Invalid"}
                      size="small"
                      className={classes.invalidChip}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box className={classes.summary}>
        <Typography variant="body2" className={classes.summaryText}>
          {invalidCount > 0 
            ? (getLang(lang, "paragraph.INVALID_SERIALS_HELP_TEXT") || 
               "Please verify the invalid serial numbers exist in your system and correct them in your CSV file.")
            : (getLang(lang, "paragraph.ALL_SERIALS_VALID") || 
               "All serial numbers are valid and ready for import.")
          }
        </Typography>
      </Box>
    </Paper>
  );
} 